/**
 * NotFound - 404 error page
 * Displays when user navigates to non-existent routes
 * Provides navigation options and help request submission
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import { apiService } from '../services/apiService';
import { logger } from '../utils/logger';
import SEO from '../components/seo/SEO';

export default function NotFound() {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleSubmitHelp = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await apiService.post('/help-requests', {
        url: window.location.href,
        referrer: document.referrer || null,
        message: message || null
      });
      setSubmitted(true);
      setShowForm(false);
    } catch (error) {
      logger.child('UI').error('Failed to submit help request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <SEO 
        title="Page Not Found - PromptShare"
        description="The page you are looking for does not exist on PromptShare."
      />
      <div className="max-w-md w-full text-center">
        {/* 404 Illustration */}
        <div className="mb-8">
          <div className="text-6xl font-bold text-primary-600 mb-4">404</div>
          <svg
            className="mx-auto h-24 w-24 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 20a7.962 7.962 0 01-5.207-1.709m0 0L6 18m0 0l-3-3m3 3v-3.5a3.5 3.5 0 00-7 0V16m14 0v3.5a3.5 3.5 0 007 0V16m-7 0l3 3m-3-3l-3 3"
            />
          </svg>
        </div>

        {/* Content */}
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          Page Not Found
        </h1>

        <p className="text-gray-600 mb-8">
          Sorry, we couldn't find the page you're looking for. 
          It might have been moved, deleted, or you entered the wrong URL.
        </p>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/">
            <Button variant="primary" size="lg">
              Go Home
            </Button>
          </Link>
          
          <Button
            onClick={handleGoBack}
            variant="outline"
            size="lg"
          >
            Go Back
          </Button>
        </div>

        {/* Helpful Links */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          <p className="text-sm text-gray-500 mb-4">
            You might be looking for:
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link
              to="/"
              className="text-primary-600 hover:text-primary-700"
            >
              Homepage
            </Link>
            <Link
              to="/prompts"
              className="text-primary-600 hover:text-primary-700"
            >
              Browse Prompts
            </Link>
            <Link
              to="/create"
              className="text-primary-600 hover:text-primary-700"
            >
              Create Prompt
            </Link>
          </div>
        </div>

        {/* Help Request Section */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          {submitted ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-700">
                Thank you! We've received your help request and will look into it.
              </p>
            </div>
          ) : showForm ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                Can't find what you're looking for? Let us know:
              </p>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what you were looking for (optional)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                rows={3}
              />
              <div className="flex gap-2 justify-center">
                <Button
                  onClick={handleSubmitHelp}
                  variant="primary"
                  size="sm"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
                <Button
                  onClick={() => setShowForm(false)}
                  variant="outline"
                  size="sm"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowForm(true)}
              className="text-sm text-primary-600 hover:text-primary-700 underline"
            >
              Need help? Let us know what you were looking for
            </button>
          )}
        </div>
      </div>
    </div>
  );
}